import React, { ComponentProps, ReactNode, useState, useLayoutEffect } from 'react';
import * as Select from '@radix-ui/react-select';
import MenuDrawer from './Menu/MenuDrawer';
import MenuItem, { MenuItemText } from './Menu/MenuItem';
import MenuContent from './Menu/MenuContent';
import MenuText from './Menu/MenuText';
import { MenuStyle } from './Menu/MenuStyle';

export type TDropdownSelectButtonProps = {
  isOpen: boolean;
  value: ReactNode;
  eyebrow?: string;
  placeholder?: string;
  dropdownSide?: 'top' | 'bottom' | null;
};

type TDropdownSelectProps = {
  mobileFooterContent?: ComponentProps<typeof MenuContent>['mobileFooterContent'];
  options: (MenuItemText & {
    disabled?: boolean;
    value?: string | null;
    icon?: string;
  })[];
  value?: string | null;
  name?: string | null;
  onValueChange?: (value: string) => void;
  align?: ComponentProps<typeof Select.Content>['align'];
  button: (props: TDropdownSelectButtonProps) => JSX.Element;
  menuStyle?: MenuStyle;
  placeholder?: string;
  label?: string;
  valueClassName?: string;
  disabled?: boolean;
  smallClassName?: string;
  required?: boolean;
  showError?: (show: boolean) => void; //error is shown on the nested button
};

const SELECTED_ICON_NAME = 'checkmark';

const DropdownSelect = ({
  button,
  align = 'end',
  menuStyle = 'standalone',
  label,
  name,
  options,
  onValueChange,
  value,
  mobileFooterContent,
  placeholder = 'Choose an option...',
  valueClassName,
  disabled = false,
  smallClassName,
  required,
  showError
}: TDropdownSelectProps) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [dropdownSide, setDropdownSide] = useState<'top' | 'bottom' | null>(null);
  const isClosedFromMouse = React.useRef(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const selectedOption = options?.find(o => o.value === value);

  useLayoutEffect(() => {
    if (!isOpen) return;

    let observer: MutationObserver | null = null;
    let raf: number;

    const check = () => {
      const sideElContent = contentRef.current;
      if (!sideElContent) {
        raf = requestAnimationFrame(check);
        return;
      }

      const updateSide = () => {
        const side = sideElContent.getAttribute('data-side');
        if (side === 'top' || side === 'bottom') {
          setDropdownSide(side);
        }
      };

      updateSide();

      observer = new MutationObserver(updateSide);
      observer.observe(sideElContent, { attributes: true, attributeFilter: ['data-side'] });
    };

    raf = requestAnimationFrame(check);

    return () => {
      if (observer) observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [isOpen]);

  const sortedOptions =
    menuStyle === 'standalone-dark'
      ? [...options]?.sort((a, b) =>
          ((a.label ?? '') as string).localeCompare((b.label ?? '') as string)
        )
      : options;

  const buttonTitle =
    (menuStyle === 'standalone-dark' || menuStyle === 'standalone-dark-unsorted') && placeholder ? (
      `${placeholder}${selectedOption?.label}`
    ) : (
      <Select.Value />
    );

  const handleOnCloseAutoFocus = React.useCallback((e: Event) => {
    /* Known issue in Radix Select where it can't tell the difference between dismissing via keyboard or mouse click https://github.com/radix-ui/primitives/issues/1803 */
    if (isClosedFromMouse.current === true) {
      e.preventDefault();
      isClosedFromMouse.current = false;
    }
  }, []);

  return (
    <Select.Root
      value={value || ''}
      defaultOpen={isOpen}
      open={isOpen}
      name={name || ''}
      onOpenChange={open => setIsOpen(open)}
      onValueChange={onValueChange}
      required={required}
    >
      <Select.Trigger asChild disabled={disabled}>
        {button({
          placeholder,
          isOpen,
          eyebrow: `${label}${required ? ' *' : ''}`,
          value: selectedOption && (
            <MenuText
              value={buttonTitle}
              eyebrow={selectedOption?.eyebrow}
              valueClassName={valueClassName}
              smallClassName={smallClassName}
            />
          ),
          dropdownSide
        })}
      </Select.Trigger>
      <MenuDrawer onClose={() => setIsOpen(false)} isOpen={isOpen} useManualTransition>
        <Select.Content
          onCloseAutoFocus={handleOnCloseAutoFocus}
          position="popper"
          align={align}
          onPointerDownOutside={() => {
            isClosedFromMouse.current = true;
            return showError && required && showError(!selectedOption);
          }}
          ref={contentRef}
        >
          <MenuContent
            mobileTitle={placeholder}
            menuStyle={menuStyle}
            onClose={() => setIsOpen(false)}
            mobileFooterContent={mobileFooterContent}
            dropdownSide={dropdownSide}
          >
            <Select.Viewport className="max-w-[100vw]">
              {sortedOptions?.map(option => {
                const isSelected = value === option.value;
                return (
                  <MenuItem
                    Element={Select.Item}
                    key={option.value || ''}
                    value={option.value || ''}
                    tooltipText={option.value || ''}
                    menuStyle={menuStyle}
                    icon={option.icon}
                    rightIcon={isSelected ? SELECTED_ICON_NAME : undefined}
                    label={<Select.ItemText>{option.label}</Select.ItemText>}
                    eyebrow={option.shortEyebrow || option.eyebrow}
                    disabled={option.disabled}
                    onClick={() => onValueChange?.(option.value || '')}
                    smallClassName={smallClassName}
                  />
                );
              })}
            </Select.Viewport>
          </MenuContent>
        </Select.Content>
      </MenuDrawer>
    </Select.Root>
  );
};

export default DropdownSelect;
